import {
	Box,
	Button,
	FormControl,
	FormErrorMessage,
	Heading,
	Image,
	InputGroup,
	Text,
} from "@chakra-ui/react";
import Input from "../components/inputs/Input";
import { Center, Flex } from "@chakra-ui/layout";
import loginImage from "../assets/login.png";
import Logo from "../components/Logo";
import PrimaryButton from "../components/PrimaryButton";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { NavLink, useNavigate } from "react-router-dom";
import PasswordInput from "../components/inputs/PasswordInput";
import StlLoader from "../components/StlLoader";
import { useQueryParams } from "../hooks/utils.hooks";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { invitation } from "../mutations/auth.mutations";
import Select from "../components/inputs/Select";
import { useTokenState } from "../hooks/tokens.hooks";
import { TokenInvitationState } from "../types/token.types";
import { fPhoneNumber } from "../lib/format";

function InvitationPage() {
	const methods = useForm();
	const navigate = useNavigate();
	const { token } = useQueryParams();
	const [role, setRole] = useState<string | undefined>("");
	const { data: tokenMetaInformation } = useTokenState(token);
	useEffect(() => {
		if (tokenMetaInformation !== null && tokenMetaInformation !== undefined) {
			setRole((tokenMetaInformation as TokenInvitationState)?.role);
			methods.setValue(
				"role",
				(tokenMetaInformation as TokenInvitationState)?.role,
			);
		}
	}, [tokenMetaInformation, methods]);
	const { mutate: mutateInvitation } = useMutation(invitation, {
		onSuccess: () => {
			navigate("/login");
		},
	});

	const onSubmit = async (data: FieldValues) => {
		const valid = await methods.trigger();
		if (valid) {
			mutateInvitation({
				token,
				firstName: data.firstName,
				lastName: data.lastName,
				role: data.role,
				password: data.password,
				phoneNumber: data.phoneNumber,
			});
		}
	};

	useEffect(() => {
		if (!token) {
			navigate("/login");
		}
	}, [navigate, token]);

	return (
		<Flex>
			<Box display={{ base: "none", lg: "block" }}>
				<StlLoader
					alternative={
						<Image
							src={loginImage}
							alt="Dan Abramov"
							h="100vh"
							w="50vw"
							display={{ base: "none", lg: "block" }}
						/>
					}
				></StlLoader>
			</Box>
			<Center w="100%">
				<Flex
					direction="column"
					alignItems="left"
					justifyContent="center"
					h="100vh"
					w="50vw"
					px={{ base: "44px", md: "144px" }}
				>
					<Flex direction="column" alignItems="center">
						<Logo />
						<Heading
							fontWeight={600}
							fontSize="32px"
							lineHeight="48px"
							color="gray.900"
							whiteSpace={"nowrap"}
						>
							Vous avez été invité à rejoindre un centre
						</Heading>
						<Text
							fontWeight={400}
							fontSize="20px"
							lineHeight="30px"
							color="gray.600"
							textAlign="center"
							display={{ base: "none", sm: "block" }}
						>
							Remplissez ce formulaire
						</Text>
					</Flex>
					<FormProvider {...methods}>
						<form
							style={{ marginTop: "32px" }}
							onSubmit={methods.handleSubmit((data) => onSubmit(data))}
						>
							<FormControl
								mt="10px"
								isInvalid={!!methods.formState.errors.lastName}
							>
								<Input
									id="lastName"
									placeholder="Nom"
									defaultValue={
										(tokenMetaInformation as TokenInvitationState)?.lastName
									}
									{...methods.register("lastName", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.email?.message}</>
								</FormErrorMessage>
							</FormControl>

							<FormControl
								mt="10px"
								isInvalid={!!methods.formState.errors.firstName}
							>
								<Input
									id="firstName"
									placeholder="Prénom"
									defaultValue={
										(tokenMetaInformation as TokenInvitationState)?.firstName
									}
									{...methods.register("firstName", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.email?.message}</>
								</FormErrorMessage>
							</FormControl>

							<FormControl
								mt="10px"
								isInvalid={!!methods.formState.errors.role}
							>
								<Select
									id="role"
									placeholder="Rôle"
									value={role}
									setValue={setRole}
									{...methods.register("role", {
										required: "Ce champ est obligatoire",
									})}
								>
									<option value="AUDIOPROSTHESIST">Audioprothésiste</option>
									<option value="ASSISTANT">Assistant</option>
									<option value="OTHER">Autre</option>
								</Select>
								<FormErrorMessage>
									<>{methods.formState.errors?.role?.message}</>
								</FormErrorMessage>
							</FormControl>

							<FormControl
								mt="10px"
								isInvalid={!!methods.formState.errors.phoneNumber}
							>
								<Input
									id="phoneNumber"
									placeholder="Téléphone"
									defaultValue={fPhoneNumber(
										(tokenMetaInformation as TokenInvitationState)
											?.phoneNumber ?? "",
									)}
									{...methods.register("phoneNumber", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.email?.message}</>
								</FormErrorMessage>
							</FormControl>

							<FormControl
								mt="10px"
								isInvalid={!!methods.formState.errors.password}
							>
								<InputGroup size="md">
									<PasswordInput
										pr="4.5rem"
										placeholder="Mot de passe"
										{...methods.register("password", {
											required: "Ce champ est obligatoire",
										})}
									/>
								</InputGroup>
								<FormErrorMessage>
									<>{methods.formState.errors?.email?.message}</>
								</FormErrorMessage>
							</FormControl>

							<PrimaryButton mt="10px" type="submit">
								Rejoindre le centre
							</PrimaryButton>
						</form>
					</FormProvider>
					<Center my="24px">
						Déjà un compte ?&nbsp;
						<Button variant="link" color="brand.700">
							<NavLink to={`/invitation/login?token=${token}`}>
								Se connecter avec un compte existant
							</NavLink>
						</Button>
					</Center>
				</Flex>
			</Center>
		</Flex>
	);
}

export default InvitationPage;
